import classNames from 'classnames'
import styled, {
  css,
  system,
  type SystemProps,
} from 'quickstart/styled-components/system'

/**
 * A transient prop threaded in by {@link reskinStyled} in `index.tsx`, so a
 * styled component can branch on which admin is rendering without the prop
 * reaching the DOM. `Table`, `P` and `Vr` take this branch; the rest of the
 * table components (`Thead`/`Tbody`/`Tfoot`/`Tr`/`Th`/`Td`) went back to
 * plain re-exports once the admin grew a table-layout class of its own
 * (tasks.md — A.10) and don't need it.
 */
interface ReskinProp {
  $reskin?: boolean
}

/*
const styled = tag => (...args) => baseStyled(tag)`
  .${ADMIN_WRAPPER_CLASSNAME} & {
    ${typeof args[0] === 'function' ? args[0] : css(...args)};
    ${system};
  }
`

Object.keys(baseStyled).forEach(k => {
  styled[k] = styled(k)
})
*/

export const P = styled.div<SystemProps & ReskinProp>`
  ${p =>
    !p.$reskin &&
    css`
      margin: 1rem 0;
    `};
  ${system};
`

// Carries the admin's table-layout class unconditionally rather than only
// under the reskin: classic's stylesheet has no `.t-table` rule, so the class
// is inert there. `table.t-table`'s own descendant selectors (`th`, `td`,
// `tbody tr`, ...) outrank this component's bare generated class wherever the
// two disagree, so nothing below needs the class repeated onto it.
//
// The margin is the one declaration that still needs $reskin: nothing in the
// admin's own rule sets a table margin, so a margin written here unconditionally
// would win by specificity over the admin's `* { margin: 0 }` reset and add
// unintended vertical space under the reskin. Omitting it under the reskin
// (rather than writing `margin: 0` explicitly) reaches the same computed
// value through that reset, the same way `P` omits its own margin below.
export const Table = styled.table.attrs({className: 't-table'})<
  SystemProps & ReskinProp
>`
  ${p =>
    !p.$reskin &&
    css`
      margin: 0.5rem 0;
    `};
  ${system};
`

// The classic grey header fill lives here rather than on `Th`: a table's
// background-painting order paints a row group (`thead`) before the row
// (`tr`), and the row before its cells (`th`) -- each later layer occluding
// the one beneath wherever it isn't transparent. The admin colors `thead tr`
// (`table.t-table thead tr { background: var(--t-neutral-100) }`), one layer
// above this. Painting classic's fill here, and leaving `Th` with no
// background of its own, means the admin's row-level color shows through
// under the reskin instead of being covered by a `th`-level classic literal
// that no admin rule exists to beat by specificity (there is no `th`
// background rule to lose to, because the admin colors the row, not the
// cell).
export const Thead = styled.thead<SystemProps>`
  background: #e6e6e6;
  ${system};
`

export const Tbody = styled.tbody<SystemProps>`
  ${system};
`

export const Tfoot = styled.tfoot<SystemProps>`
  ${system};
`

export const Tr = styled.tr<SystemProps>`
  ${system};
`

export const Th = styled.th<SystemProps>`
  padding: 0.25rem 0.5rem;
  text-align: left;
  font-weight: bold;
  && > * {
    margin: 0;
  }
  ${system};
`

export const Td = styled.td<SystemProps>`
  padding: 0.25rem 0.5rem;
  && > * {
    margin: 0;
  }
  ${system};
`

export const Label = styled.label<SystemProps>`
  // inline-block makes marginBottom effective, and also allows enclosed items
  // with vertical-align: middle to line up properly (checkboxes, radios)
  display: inline-block;
  margin-bottom: 0.3rem;
  margin-right: 0.75em;
  ${system}
`

export const GroupWrapper = styled.div<SystemProps>`
  // Hide hr on global configs
  hr {
    display: none;
    .dialogbox & {
      display: block;
    }
  }
  ${system}
`

const groupPadding = '20px'

export const Group = styled.div<SystemProps>`
  margin-bottom: 1rem;
  padding-left: ${groupPadding};
  padding-right: ${groupPadding};
  // Don't apply top margin to first group on global configs
  .dialogbox &,
  &:not(:first-child) {
    margin-top: 1rem;
  }
  ${system}
`

export const GroupTitle = styled.div<SystemProps>`
  font-weight: bold;
  margin-bottom: 0.5rem;
  ${system}
`

// Hack to allow Subgroup to reference itself without blowing up TS
const SubGroupInner = styled.div``

export const SubGroup = styled(SubGroupInner)<
  SystemProps & {outlined?: boolean; shaded?: boolean}
>(
  ({outlined, shaded}) => css`
    ${
      outlined || shaded
        ? css`
        padding: 0.75rem;
        ${outlined && {border: '1px solid #7c7c7c'}};
        ${shaded && {background: '#e6e6e6'}};
        margin-top: 0.5rem;
        margin-bottom: 0.5rem;
      `
        : css`
        margin-top: 1rem;
        margin-bottom: 1rem;
        ${SubGroupInner} > & {
          margin-top: 0.3rem;
          margin-bottom: 0.3rem;
        }
      `
    };
    & > *:first-child {
      margin-top: 0;
    }
    & > *:last-child {
      margin-bottom: 0;
    }
    ${system}
  `,
)

export const SubGroupTitle = styled.div<SystemProps>`
  font-weight: bold;
  ${system}
`

export const AdminField = styled.div<{hidden?: boolean; snug?: boolean}>(
  ({hidden, snug}) => css`
    ${hidden && {display: 'none'}};

    ${
      !snug &&
      css`
      margin-top: 0.68rem;
      margin-bottom: 0.68rem;
      ${SubGroup} > & {
        margin-top: 0.3rem;
        margin-bottom: 0.3rem;
      }
    `
    };

    &,
    div,
    input,
    select {
      max-width: 100%;
    }
  `,
)

export const Option = styled.div<SystemProps>`
  margin-top: 0.125rem;
  margin-bottom: 0.125rem;
  ${system}
`

export const OptionLabel = styled.span<SystemProps & {left?: boolean}>(
  ({left}) => css`
    vertical-align: middle;
    ${left ? {marginRight: '0.5em'} : {marginLeft: '0.5em'}}
    ${system}
  `,
)

export const Input = styled.input.attrs<{long?: boolean}>(
  ({className, long}) => ({
    className: classNames(long && 'input-long', className),
  }),
)<SystemProps>(
  ({type}) => css`
    ${(type === 'checkbox' || type === 'radio') && {verticalAlign: 'middle'}}
    ${system}
  `,
)

export const TabStrip = styled.div<SystemProps>`
  background: #b3b3b3;
  border-top: 2px solid #b3b3b3;
  margin-bottom: 1rem;
  magin-top: 0.5rem;
  ${system}
`

export const InputButton = styled.input.attrs(
  ({children, className, ...props}) => ({
    type: 'button',
    value: `${children}`,
    ...props,
    // Classic's own button class, restored here: the reskin branch no longer
    // reaches this component at all (it renders a plain `<button
    // class="t-button">` instead — see `Dropdown`), so this class is
    // unconditionally classic's.
    className: classNames('button', className),
    children: undefined,
  }),
)<SystemProps>`
  ${system}
`

const BellyButton = styled.button.attrs({type: 'button'})``

// This styling matches an admin input/select.
//
// `minHeight` is measured against a real classic control beside it (the nav
// tree's own label/href inputs, `input, textarea` in classic/tizra.css,
// padding 2px) rather than copied from a line of text: the earlier 13.5px
// was the content height of text, which doesn't transfer to a trigger whose
// content is an icon — it rendered a px short of a real control's own 18px
// border-box height (tasks.md — 4.3). The reskin pair needs no such
// settling: `.t-button` and `.t-input` already agree at 36px.
const classicInputStyle = {
  border: '1px solid #c3c3c3',
  borderTop: '1px solid #7c7c7c',
  padding: '2px 2px 2px 2px',
  minHeight: '18px',
  fontFamily: 'verdana, sans-serif',
  fontSize: '100%',
}

export const SimpleButton = styled(BellyButton)<SystemProps>`
  background-color: inherit;
  ${classicInputStyle}
  ${system}
`

interface TabButtonProps {
  active?: boolean
}

export const TabButton = styled(BellyButton)<SystemProps & TabButtonProps>(
  ({active}) => css`
    background: ${active ? 'white' : 'inherit'};
    border: none;
    border-right: 2px solid #777;
    font-weight: bold;
    padding-top: calc(0.5rem - 2px); // top border
    padding-bottom: 0.5rem;
    padding-right: 1.5rem;
    padding-left: 1.5rem;
    &:first-child {
      padding-left: ${groupPadding};
    }
    ${system}
  `,
)

export const TabPane = styled.div<SystemProps & {active?: boolean}>(
  ({active}) => css`
    ${!active && {display: 'none'}}
    margin: 1rem 0;
    ${system}
  `,
)

export const SubTabStrip = styled.div<SystemProps>`
  display: flex;
  margin-bottom: 1rem;
  magin-top: 0.5rem;
`

interface SubTabButtonProps {
  active?: boolean
  onClick?: any
}

export const SubTabButton = styled.a.attrs(
  ({active, onClick}: SubTabButtonProps) => ({
    ...(active ? {as: 'span'} : {href: '#'}),
    onClick: (e: any) => {
      e.preventDefault()
      if (onClick) {
        return onClick(e)
      }
    },
  }),
)<SystemProps & SubTabButtonProps>(
  ({active}) => css`
    font-weight: bold;
    padding: 0 0.5rem;
    &:first-child {
      padding-left: ${groupPadding};
    }
    ${
      active
        ? css`
        span {
          text-decoration: underline 2px;
          text-underline-offset: 4px;
        }
      `
        : css`
        &:active,
        &:hover {
          text-decoration: none; /* cancel tizra.css */
          span {
            text-decoration: underline 2px;
            text-underline-offset: 4px;
          }
        }
      `
    }
    ${system};
  `,
)

export const SubTabPane = styled.div<SystemProps & {active?: boolean}>(
  ({active}) => css`
    ${!active && {display: 'none'}}
    margin: 1rem 0;
    ${system}
  `,
)

export const Hint = styled.div<SystemProps>`
  margin-top: 0.3rem;
  margin-bottom: 1rem;
  font-style: italic;
  ${system}
`

export const Row = styled.div<SystemProps>`
  display: flex;
  gap: 0.75rem;
  ${system};
`

export const Column = styled.div<SystemProps>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  ${system};
`

export const Stack = styled.div<SystemProps & {row?: boolean}>(
  ({row}) => css`
    display: flex;
    flex-direction: ${row ? 'row' : 'column'};
    gap: 1rem;
    flex: 1 1 auto;
    align-self: stretch;
    ${system};
  `,
)

export const Vr = styled.div<SystemProps & ReskinProp>`
  display: inline-block;
  align-self: stretch;
  flex-grow: 0;
  width: 1px;
  min-height: 1em;
  ${p =>
    p.$reskin
      ? css`
          background-color: var(--t-neutral-200);
        `
      : css`
          background-color: currentColor;
          opacity: 0.25;
        `};
  ${system};
`

export const Box = styled.div<SystemProps>`
  ${system}
`

export const Menu = styled.div`
  position: relative;
  z-index: 50;
  display: flex;
  max-height: var(--popover-available-height);
  flex-direction: column;
  overflow: auto;
  overscroll-behavior: contain;
  border: 1px solid black;
  background-color: white;
  padding: 0.2rem;
  color: black;
  outline: none !important;
`

export const MenuItem = styled.div`
  display: flex;
  cursor: default;
  scroll-margin: 0.5rem;
  align-items: center;
  gap: 0.5rem;
  border-radius: 0.15rem;
  padding: 0.3rem;
  outline: none !important;

  &[aria-disabled='true'] {
    opacity: 0.25;
  }

  &[data-active-item] {
    background-color: hsl(204 100% 40%);
    color: hsl(204 20% 100%);
  }

  &:active,
  &[data-active] {
    background-color: hsl(204 100% 32%);
  }
`
